import { useInView } from '@/hooks/reveal-in-view';
import NextImage from 'next/image';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';

import type { LeadGenTranslations } from '@/translations';
import type { ContentfulCardWithImageType, ParsedContentNode } from '@/types';

import { ContentManager } from '../content-manager';
import { Image } from '../image';

import { ApplicationProcessForm } from './application-process-form';
import { BookDemoForm } from './book-demo-form';

type ScreenSize = 'mobile' | 'tablet' | 'desktop';

interface Props {
  cards: ContentfulCardWithImageType[];
  title: string;
  translation: LeadGenTranslations;
}

interface CardProps {
  title?: ParsedContentNode[];
}

const Card: FC<PropsWithChildren<CardProps>> = ({ children, title }) => {
  return (
    <article className="how-to-proceed--card" role="tabpanel">
      {title && (
        <ContentManager
          content={title}
          className="how-to-proceed--card--title"
        />
      )}
      <div className="how-to-proceed--card--content">{children}</div>
    </article>
  );
};

const mobileTabs = ['Demo', 'Call', 'Apply'];

export const HowToProceed: FC<Props> = ({ cards, title, translation }) => {
  const [screenSize, setScreenSize] = useState<ScreenSize>('mobile');
  const [selectedTab, setSelectedTab] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  const tabs =
    screenSize === 'mobile'
      ? mobileTabs
      : cards.map(
          (card) => card.title?.[0].html?.replaceAll(/<p>|<\/p>/g, '') ?? '',
        );

  useEffect(() => {
    const tabletMedia = window.matchMedia('(min-width: 768px)');
    const desktopMedia = window.matchMedia('(min-width: 1024px)');

    if (desktopMedia.matches) setScreenSize('desktop');
    else if (tabletMedia.matches) setScreenSize('tablet');

    const handleTabletMedia = () => {
      if (!tabletMedia.matches || !desktopMedia.matches) {
        setScreenSize(tabletMedia.matches ? 'tablet' : 'mobile');
      }
    };
    const handleDesktopMedia = () => {
      if (desktopMedia.matches || tabletMedia.matches) {
        setScreenSize(desktopMedia.matches ? 'desktop' : 'tablet');
      }
    };

    tabletMedia.addEventListener('change', handleTabletMedia);
    desktopMedia.addEventListener('change', handleDesktopMedia);

    return () => {
      tabletMedia.removeEventListener('change', handleTabletMedia);
      desktopMedia.removeEventListener('change', handleDesktopMedia);
    };
  }, []);

  return (
    <section className="how-to-proceed" id="how-to-proceed" ref={ref}>
      <div className="how-to-proceed--wrapper">
        <h2
          style={{
            transform: isInView ? 'none' : 'translateY(20px)',
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
          className="how-to-proceed--title"
        >
          {title}
        </h2>

        {screenSize !== 'desktop' && (
          <nav className="how-to-proceed--tabs" role="tablist">
            {tabs.map((tab, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                onClick={() => setSelectedTab(index)}
                className={`how-to-proceed--tab${selectedTab === index ? ' how-to-proceed--tab_selected' : ''}`}
                dangerouslySetInnerHTML={{ __html: tab }}
              />
            ))}
          </nav>
        )}

        <div
          className="how-to-proceed--cards-wrapper"
          role="region"
          style={{
            transform: `translate(${screenSize === 'desktop' ? 0 : selectedTab * -100}vw, ${isInView ? '0' : '20px'})`,
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
        >
          <Card title={cards[0].title}>
            <BookDemoForm />
          </Card>

          <Card title={cards[1].title}>
            {cards[1].image && (
              <a
                href={translation.howToProceed.linkedIn.link?.url}
                rel="nofollow noreferrer"
                target="_blank"
                title={translation.howToProceed.linkedIn.text}
                className="how-to-proceed--card--link-btn"
              >
                <Image
                  src={cards[1].image as any}
                  alt={cards[1].image.title}
                  className="how-to-proceed--card--content--img"
                />
                <NextImage
                  src="/images/linkedin-logo.svg"
                  alt="LinkedIn"
                  width={56}
                  height={56}
                  className="how-to-proceed--card--content--icon"
                />
              </a>
            )}
            {cards[1].description && (
              <ContentManager content={cards[1].description} />
            )}

            <div className="how-to-proceed--card--btn-wrapper">
              <a
                href={translation.howToProceed.schedule.link?.url}
                rel="nofollow noreferrer"
                target="_blank"
                title={translation.howToProceed.schedule.text}
                className="link link_full link_blue how-to-proceed--card--btn"
              >
                <span>{translation.howToProceed.schedule.text}</span>
              </a>
            </div>
          </Card>

          <Card title={cards[2].title}>
            {cards[2].description && (
              <ContentManager content={cards[2].description} />
            )}
            <ApplicationProcessForm />
          </Card>
        </div>
      </div>
    </section>
  );
};
